import type { Todo } from "../../domain/entities.ts";
import { childTasks } from "./task-hierarchy.ts";

export interface TaskLifecycleFields<Field> {
  archivedAt: Field;
  pinned: Field;
  completion: Field[];
  deleted: Field;
}

export interface TaskLifecycleCapabilities<Snapshot, Field> {
  tasks(): Todo[];
  activeRoot(): string | null;
  findPersisted(id: string, rootId: string): Promise<Todo | undefined>;
  mutate(id: string, update: (todo: Todo) => void): Promise<Todo | undefined>;
  changeFamily?(tasks: Todo[], update: (todo: Todo) => void): Promise<void>;
  change(id: string, rootId: string, update: (todo: Todo) => void): Promise<unknown>;
  complete(todo: Todo): Promise<string | null>;
  createNextOccurrence(todo: Todo): Promise<string | null>;
  blockers(todo: Todo): Todo[];
  approvalRequired?(todo: Todo): boolean;
  snapshot(todo: Todo): Snapshot;
  taskUndo(message: string, rootId: string, before: Snapshot[], fields: Field[], generatedIds?: string[]): void;
  actionUndo(message: string, undo: () => void | Promise<void>): void;
  confirmDiscard(): Promise<boolean>;
  confirmOpenChildren?(count: number): Promise<boolean>;
  clearSelected(): void;
  resetPanel(): void;
  render(animate?: boolean): Promise<void>;
  updateSubtask(todo: Todo, subtaskId: string, trigger: HTMLElement): void;
  toast(message: string, error?: boolean): void;
  sync(): void;
  now(): number;
  fields: TaskLifecycleFields<Field>;
}

export function createTaskLifecycleController<Snapshot, Field>(capabilities: TaskLifecycleCapabilities<Snapshot, Field>) {
  async function toggleArchive(id: string): Promise<void> {
    const todo = capabilities.tasks().find(item => item.id === id);
    if (!todo) return;
    const family = [todo, ...childTasks(capabilities.tasks(), todo)];
    const before = family.map(capabilities.snapshot);
    const archivedAt = todo.archivedAt ? null : capabilities.now();
    if (capabilities.changeFamily) await capabilities.changeFamily(family, draft => { draft.archivedAt = archivedAt; });
    else for (const member of family) await capabilities.mutate(member.id, draft => { draft.archivedAt = archivedAt; });
    capabilities.clearSelected();
    await capabilities.render();
    capabilities.taskUndo(todo.archivedAt ? "Task restored" : "Task archived", todo.rootId, before, [capabilities.fields.archivedAt]);
    capabilities.sync();
  }

  async function togglePin(id: string): Promise<void> {
    const todo = capabilities.tasks().find(item => item.id === id);
    if (!todo) return;
    const before = capabilities.snapshot(todo);
    await capabilities.mutate(id, draft => { draft.pinned = !draft.pinned; });
    await capabilities.render(false);
    capabilities.taskUndo(todo.pinned ? "Task unpinned" : "Task pinned", todo.rootId, [before], [capabilities.fields.pinned]);
    capabilities.sync();
  }

  async function toggleSubtask(taskId: string, subtaskId: string, trigger: HTMLElement): Promise<void> {
    const original = capabilities.tasks().find(todo => todo.id === taskId);
    if (!original) return;
    const wasCompleted = original.subtasks?.find(item => item.id === subtaskId)?.completed;
    const updated = await capabilities.mutate(taskId, draft => {
      const subtask = (draft.subtasks || []).find(item => item.id === subtaskId);
      if (subtask) subtask.completed = !subtask.completed;
    });
    if (!updated) return;
    const automatedCompletion = !original.completed && updated.completed;
    const generatedId = automatedCompletion ? await capabilities.createNextOccurrence(updated) : null;
    if (automatedCompletion) await capabilities.render(false);
    else capabilities.updateSubtask(updated, subtaskId, trigger);
    if (wasCompleted !== undefined) {
      capabilities.actionUndo(wasCompleted ? "Subtask reopened" : "Subtask completed", async () => {
        await capabilities.change(taskId, original.rootId, todo => {
          const item = (todo.subtasks || []).find(candidate => candidate.id === subtaskId);
          if (item) item.completed = wasCompleted;
          if (automatedCompletion) { todo.completed = false; todo.completedAt = null; }
        });
        if (generatedId) await capabilities.change(generatedId, original.rootId, todo => { todo.deleted = true; });
      });
    }
    capabilities.sync();
  }

  async function toggleComplete(id: string): Promise<void> {
    const todo = capabilities.tasks().find(item => item.id === id);
    if (!todo) return;
    if (!todo.completed && capabilities.approvalRequired?.(todo)) {
      capabilities.toast("Request and receive approval before completing this task", true);
      return;
    }
    if (!todo.completed && capabilities.blockers(todo).length) {
      capabilities.toast("Complete this task’s blockers first", true);
      return;
    }
    const openChildren = childTasks(capabilities.tasks(), todo).filter(child => !child.completed);
    if (!todo.completed && openChildren.length && capabilities.confirmOpenChildren && !await capabilities.confirmOpenChildren(openChildren.length)) return;
    const before = capabilities.snapshot(todo);
    let generatedId: string | null = null;
    if (todo.completed) await capabilities.mutate(id, draft => { draft.completed = false; draft.completedAt = null; });
    else generatedId = await capabilities.complete(todo);
    await capabilities.render();
    capabilities.taskUndo(todo.completed ? "Task reopened" : "Task completed", todo.rootId, [before], capabilities.fields.completion, generatedId ? [generatedId] : []);
    capabilities.sync();
  }

  async function deleteTask(id: string): Promise<void> {
    if (!await capabilities.confirmDiscard()) return;
    const rootId = capabilities.activeRoot();
    if (!rootId) return;
    const todo = await capabilities.findPersisted(id, rootId);
    if (!todo) return;
    const family = [todo, ...childTasks(capabilities.tasks(), todo, true)];
    const before = family.map(capabilities.snapshot);
    if (capabilities.changeFamily) await capabilities.changeFamily(family, draft => { draft.deleted = !todo.deleted; });
    else for (const member of family) await capabilities.mutate(member.id, draft => { draft.deleted = !todo.deleted; });
    capabilities.resetPanel();
    await capabilities.render();
    capabilities.taskUndo(todo.deleted ? "Task restored" : "Task moved to trash", rootId, before, [capabilities.fields.deleted]);
    capabilities.sync();
  }

  return { toggleArchive, togglePin, toggleSubtask, toggleComplete, deleteTask };
}
